/**
 * Hook to get combined notifications summary (friend requests + unread chats)
 * Replaces separate useFriendRequestCount and unread count polling
 * 
 * Phase 3: Uses React Query with SWR for automatic caching and background refetching
 */
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '../../auth/hooks/useAuth';
import { getNotificationsSummary, NotificationsSummary } from '../services/notificationsApi';

export function useNotificationsSummary() {
  const { session, sessionEstablishing } = useAuth();

  // Phase 3: Use React Query with SWR
  const { 
    data: summary = {
      pendingFriendRequests: 0,
      unreadConversationCount: 0,
      hasUnread: false,
      lastUpdated: new Date().toISOString(),
    },
    isLoading,
    error,
    refetch: refresh
  } = useQuery<NotificationsSummary>({
    queryKey: ['notificationsSummary'],
    queryFn: () => getNotificationsSummary(session!.access_token),
    enabled: !!session?.access_token && !sessionEstablishing,
    staleTime: 60000, // 1 minute (matches polling frequency)
    gcTime: 300000, // 5 minutes (keep in cache)
    refetchInterval: 60000, // Poll every 60 seconds
    refetchIntervalInBackground: false, // Only poll when tab is visible
    retry: 2,
    // SWR: Keep previous data while refetching (no loading state on refetch)
    placeholderData: (previousData) => previousData,
  });

  return {
    summary,
    isLoading,
    error: error ? (error instanceof Error ? error.message : 'Failed to load notifications') : null,
    refresh: () => refresh(),
  };
}
